import { Button } from 'cpool-components/input/Button';
import type { ApplicantCreation } from 'cpool-schemas';
import type { Path, useFormContext } from 'react-hook-form';
import { useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LuPlus, LuTrash2 } from 'react-icons/lu';

import { FormDateInput } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormDateInput';
import { FormCurrencyInput } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormCurrencyInput';

type RealEstateLoansProps = {
    realEstateIndex: number;
    control: ReturnType<typeof useFormContext<ApplicantCreation>>['control'];
};

export const RealEstateLoans: React.FC<RealEstateLoansProps> = ({ control, realEstateIndex }) => {
    const { t } = useTranslation();

    const { append, fields, remove } = useFieldArray({
        control,
        keyName: 'darlehenId',
        name: `immobilien.${realEstateIndex}.darlehen` as const,
    });

    const addLoan = () => {
        append({
            rateMonatlich: null,
            restschuld: null,
            zinsbindungBis: null,
        });
    };

    return (
        <div className="mt-4 pt-4 border-t border-grey-outline-light">
            <Button
                type="button"
                variant="secondary"
                onClick={addLoan}
                iconLeft={<LuPlus className="w-3 h-3" />}
                className="mb-2"
            >
                {t('REAL_ESTATE.BUTTONS.ADD_LOAN')}
            </Button>

            {fields.map((loan, loanIndex) => (
                <div key={loan.darlehenId} className="bg-grey-light rounded p-3 mb-2 space-y-2">
                    <div className="flex items-center justify-between">
                        <h5 className="text-sm font-semibold text-blue-dark">
                            {t('REAL_ESTATE.LABELS.LOAN', { count: loanIndex + 1 })}
                        </h5>
                        <button
                            type="button"
                            onClick={() => remove(loanIndex)}
                            className="p-1 text-alert-error hover:bg-alert-error/10 rounded"
                        >
                            <LuTrash2 className="w-4 h-4" />
                        </button>
                    </div>

                    <FormCurrencyInput
                        id={
                            `immobilien.${realEstateIndex}.darlehen.${loanIndex}.rateMonatlich` as Path<ApplicantCreation>
                        }
                        control={control}
                        label={t('REAL_ESTATE.LABELS.RATE')}
                        min={0}
                    />

                    <FormCurrencyInput
                        id={`immobilien.${realEstateIndex}.darlehen.${loanIndex}.restschuld` as Path<ApplicantCreation>}
                        control={control}
                        label={t('REAL_ESTATE.LABELS.RESIDUAL_DEBT')}
                        min={0}
                    />

                    <FormDateInput
                        id={
                            `immobilien.${realEstateIndex}.darlehen.${loanIndex}.zinsbindungBis` as Path<ApplicantCreation>
                        }
                        control={control}
                        label={t('REAL_ESTATE.LABELS.FIXED_INTEREST_UNTIL')}
                    />
                </div>
            ))}
        </div>
    );
};
